import React, { useState, useEffect, useRef } from 'react';
import { hrmAPI } from '../../lib/api';
import { Plus, Edit, Trash2, Search, RefreshCw, ChevronLeft, ChevronRight, Copy, X, Save, ArrowLeft, FileDown, Download, Eye, GripVertical } from 'lucide-react';
import { exportMovementsPDF } from '../../lib/export';

// Helper function to get local date string in YYYY-MM-DD format
const toLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Helper function to check if a date is a holiday
const isHolidayDate = (date, holidays) => {
    if (!date || !holidays || holidays.length === 0) return false;
    const dateStr = toLocalDateString(date);
    return holidays.some(h => {
        if (h.end_date) {
            // Check if date falls within the range
            return dateStr >= h.date && dateStr <= h.end_date;
        }
        return h.date === dateStr;
    });
};

const Movements = () => {
    const [movements, setMovements] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showDateModal, setShowDateModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [showCopyDayModal, setShowCopyDayModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Calendar state
    const [currentDate, setCurrentDate] = useState(new Date());
    const [filterDate, setFilterDate] = useState(toLocalDateString(new Date())); // Default to today
    
    // Excel-like grid state
    const [showGridView, setShowGridView] = useState(false);
    const [gridDate, setGridDate] = useState(null);
    const [gridData, setGridData] = useState({});
    const [savingRows, setSavingRows] = useState({});
    const [createAttendance, setCreateAttendance] = useState(false);
    const [saveSettingsTime, setSaveSettingsTime] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');
    
    // Drag and drop state for grid view
    const [orderedEmployeeIds, setOrderedEmployeeIds] = useState([]);
    const [draggedItem, setDraggedItem] = useState(null);
    const dragItem = useRef();
    const dragOverItem = useRef();
    
    const [formData, setFormData] = useState({
        employee_id: '',
        date: '',
        description: '',
        purpose: ''
    });

    // Duplicate form data
    const [duplicateData, setDuplicateData] = useState({
        employee_ids: [],
        date: '',
        description: '',
        purpose: ''
    });

    // Copy day form data
    const [copyDayData, setCopyDayData] = useState({
        from_date: '',
        to_date: ''
    });

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [movRes, empRes, holRes] = await Promise.all([
                hrmAPI.getMovements(),
                hrmAPI.getEmployees(),
                hrmAPI.getHolidays()
            ]);
            setMovements(movRes.data.data || movRes.data);
            setEmployees(empRes.data.data || empRes.data);
            const holData = holRes.data.data || holRes.data;
            setHolidays(holData.data || holData);
        } catch (err) { setError('Failed to load data'); }
        finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await hrmAPI.updateMovement(editingId, formData);
            } else {
                await hrmAPI.createMovement(formData);
            }
            setShowModal(false);
            resetForm();
            fetchData();
        } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    };

    const handleDuplicateSubmit = async (e) => {
        e.preventDefault();
        try {
            for (const employeeId of duplicateData.employee_ids) {
                await hrmAPI.createMovement({
                    employee_id: employeeId,
                    date: duplicateData.date,
                    description: duplicateData.description,
                    purpose: duplicateData.purpose
                });
            }
            setShowDuplicateModal(false);
            resetDuplicateForm();
            fetchData();
        } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    };

    const handleCopyDaySubmit = async (e) => {
        e.preventDefault();
        try {
            // Get all movements from the source date
            const sourceMovements = movements.filter(mov => {
                const movDate = mov.date ? mov.date.split('T')[0] : '';
                return movDate === copyDayData.from_date;
            });

            if (sourceMovements.length === 0) {
                setError('No movements found for the source date');
                return;
            }

            // Create new movements for each employee on the target date
            for (const mov of sourceMovements) {
                await hrmAPI.createMovement({
                    employee_id: mov.employee_id,
                    date: copyDayData.to_date,
                    description: mov.description,
                    purpose: mov.purpose
                });
            }

            setShowCopyDayModal(false);
            setCopyDayData({ from_date: '', to_date: '' });
            fetchData();
        } catch (err) { setError(err.response?.data?.message || 'Failed to copy movements'); }
    };

    const handleEdit = (mov) => {
        // Extract date in YYYY-MM-DD format for the date input
        const editDate = mov.date ? (mov.date.split('T')[0]) : '';
        setFormData({
            employee_id: mov.employee_id || '',
            date: editDate,
            description: mov.description || '',
            purpose: mov.purpose || ''
        });
        setEditingId(mov.id);
        setShowModal(true);
    };

    const handleDuplicate = (mov) => {
        // Extract date in YYYY-MM-DD format for the date input
        const dupDate = mov.date ? (mov.date.split('T')[0]) : '';
        setDuplicateData({
            employee_ids: [],
            date: dupDate,
            description: mov.description || '',
            purpose: mov.purpose || ''
        });
        setShowDuplicateModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            await hrmAPI.deleteMovement(id);
            fetchData();
        } catch (err) { setError('Failed to delete'); }
    };

    const resetForm = () => {
        setFormData({
            employee_id: '',
            date: '',
            description: '',
            purpose: ''
        });
        setEditingId(null);
    };

    const resetDuplicateForm = () => {
        setDuplicateData({
            employee_ids: [],
            date: '',
            description: '',
            purpose: ''
        });
    };

    const getEmployeeName = (id) => employees.find(e => e.id === id)?.name || '-';

    const handleExportPDF = () => {
        exportMovementsPDF(filteredMovements, employees, 'Movement Report', {
            preview: true,
            onPreview: (url, filename) => {
                setPreviewUrl(url);
                setPreviewFilename(filename);
            }
        });
    };

    const handleDownloadPDF = () => {
        if (previewUrl) {
            const link = document.createElement('a');
            link.href = previewUrl;
            link.download = previewFilename;
            link.click();
        }
    };

    const closePreview = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        setPreviewFilename('');
    };

    const filteredMovements = movements.filter(mov => {
        // Filter by date if selected
        if (filterDate) {
            const movDate = mov.date ? mov.date.split('T')[0] : '';
            if (movDate !== filterDate) return false;
        }
        // Filter by search term
        return getEmployeeName(mov.employee_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
            mov.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            mov.purpose?.toLowerCase().includes(searchTerm.toLowerCase());
    });

    // Calendar functions
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        
        // For Saturday start, adjust the starting day
        // getDay() returns 0=Sunday, 1=Monday, etc. Convert to Saturday=0
        let startingDay = firstDay.getDay() - 6;
        if (startingDay < 0) startingDay += 7;
        
        const days = [];
        
        for (let i = 0; i < startingDay; i++) {
            days.push(null);
        }
        
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        
        return days;
    };

    const getMovementsForDate = (date) => {
        if (!date) return [];
        const dateStr = toLocalDateString(date);
        return movements.filter(mov => mov.date && mov.date.split('T')[0] === dateStr);
    };

    const handleDateClick = (date) => {
        if (!date) return;
        // Set filter date and open grid view
        setFilterDate(toLocalDateString(date));
        openGridView(date);
    };

    const openGridView = (date) => {
        setGridDate(date);
        setShowGridView(true);
        
        // Initialize grid data with existing movements
        const dateStr = toLocalDateString(date);
        const dateMovements = movements.filter(mov => mov.date && mov.date.split('T')[0] === dateStr);
        
        const initialGridData = {};
        employees.forEach(emp => {
            const existingMov = dateMovements.find(m => m.employee_id === emp.id);
            if (existingMov) {
                initialGridData[emp.id] = {
                    id: existingMov.id,
                    description: existingMov.description || 'Absent',
                    purpose: existingMov.purpose || '',
                    hasRecord: true,
                    sort_order: existingMov.sort_order
                };
            } else {
                initialGridData[emp.id] = {
                    id: null,
                    description: '',
                    purpose: '',
                    hasRecord: false,
                    sort_order: null
                };
            }
        });
        setGridData(initialGridData);
        
        // Load order from database (sort_order) or localStorage fallback
        const movementsWithOrder = dateMovements.filter(m => m.sort_order !== null && m.sort_order !== undefined);
        
        if (movementsWithOrder.length > 0) {
            // Sort by sort_order from database
            const sortedMovements = [...dateMovements].sort((a, b) => {
                const orderA = a.sort_order ?? 999;
                const orderB = b.sort_order ?? 999;
                return orderA - orderB;
            });
            setOrderedEmployeeIds(sortedMovements.map(m => m.employee_id));
        } else {
            // Fallback to localStorage
            const savedOrder = localStorage.getItem(`movement_order_${dateStr}`);
            if (savedOrder) {
                try {
                    const parsedOrder = JSON.parse(savedOrder);
                    // Filter to only include current employees
                    const validOrder = parsedOrder.filter(id => employees.some(emp => emp.id === id));
                    // Add any new employees not in saved order
                    const existingIds = new Set(validOrder);
                    const newIds = employees.filter(emp => !existingIds.has(emp.id)).map(emp => emp.id);
                    setOrderedEmployeeIds([...validOrder, ...newIds]);
                } catch (e) {
                    setOrderedEmployeeIds(employees.map(emp => emp.id));
                }
            } else {
                setOrderedEmployeeIds(employees.map(emp => emp.id));
            }
        }
    };
    
    // Drag and drop handlers
    const handleDragStart = (e, position) => {
        dragItem.current = position;
        setDraggedItem(position);
        e.dataTransfer.effectAllowed = 'move';
    };
    
    const handleDragEnter = (e, position) => {
        dragOverItem.current = position;
        e.preventDefault();
    };
    
    const handleDragEnd = () => {
        dragItem.current = null;
        dragOverItem.current = null;
        setDraggedItem(null);
    };
    
    const handleSort = async () => {
        const _orderedEmployeeIds = [...orderedEmployeeIds];
        const draggedItemContent = _orderedEmployeeIds[dragItem.current];
        _orderedEmployeeIds.splice(dragItem.current, 1);
        _orderedEmployeeIds.splice(dragOverItem.current, 0, draggedItemContent);
        setOrderedEmployeeIds(_orderedEmployeeIds);
        
        // Auto-save order to database via API
        if (gridDate) {
            const dateStr = toLocalDateString(gridDate);
            
            // Update sort_order for each movement
            try {
                for (let index = 0; index < _orderedEmployeeIds.length; index++) {
                    const empId = _orderedEmployeeIds[index];
                    const rowData = gridData[empId];
                    
                    if (rowData && rowData.id) {
                        await hrmAPI.updateMovement(rowData.id, {
                            sort_order: index
                        });
                    }
                }
                // Refresh data to get updated sort_order values
                await fetchData();
            } catch (error) {
                console.error('Error saving sort order:', error);
            }
            
            // Also save to localStorage as backup
            localStorage.setItem(`movement_order_${dateStr}`, JSON.stringify(_orderedEmployeeIds));
        }
    };
    
    // Export PDF with custom order from grid view
    const handleExportGridPDF = () => {
        if (!gridDate) {
            alert('Please select a date first');
            return;
        }
        
        const dateStr = toLocalDateString(gridDate);
        
        // Get movements from the state for this date
        const dbMovements = movements.filter(mov => 
            mov.date && mov.date.split('T')[0] === dateStr
        );
        
        // Create ordered list combining DB records with grid data
        const orderedMovements = orderedEmployeeIds
            .map(empId => {
                // First check gridData (includes both saved and unsaved)
                const rowData = gridData[empId];
                if (rowData && (rowData.hasRecord || rowData.description)) {
                    return {
                        id: rowData.id || null,
                        employee_id: empId,
                        date: dateStr,
                        description: rowData.description || 'Absent',
                        purpose: rowData.purpose || ''
                    };
                }
                // Then check DB movements (fallback for saved records not in grid)
                const dbMov = dbMovements.find(m => m.employee_id === empId);
                if (dbMov) {
                    return {
                        id: dbMov.id,
                        employee_id: dbMov.employee_id,
                        date: dbMov.date,
                        description: dbMov.description || 'Absent',
                        purpose: dbMov.purpose || ''
                    };
                }
                return null;
            })
            .filter(m => m !== null);
        
        if (orderedMovements.length === 0) {
            alert('No movement data to export. Please add some records first.');
            return;
        }
        
        // Export directly without preview first
        exportMovementsPDF(orderedMovements, employees, `Movement Report - ${dateStr}`, {
            preview: true,
            onPreview: (url, filename) => {
                setPreviewUrl(url);
                setPreviewFilename(filename);
            }
        });
    };
    
    // Reset order to default
    const handleResetOrder = async () => {
        if (gridDate) {
            const dateStr = toLocalDateString(gridDate);
            const dateMovements = movements.filter(mov => 
                mov.date && mov.date.split('T')[0] === dateStr
            );
            
            // Clear sort_order in database
            try {
                for (const mov of dateMovements) {
                    if (mov.id) {
                        await hrmAPI.updateMovement(mov.id, {
                            sort_order: null
                        });
                    }
                }
                await fetchData();
            } catch (error) {
                console.error('Error resetting sort order:', error);
            }
            
            // Clear localStorage
            localStorage.removeItem(`movement_order_${dateStr}`);
            setOrderedEmployeeIds(employees.map(emp => emp.id));
        }
    };

    const handleGridRowSave = async (employeeId) => {
        const rowData = gridData[employeeId];
        const description = rowData.description ? rowData.description.trim() : 'Absent';

        try {
            setSavingRows({...savingRows, [employeeId]: true});
            
            // Get settings for check_in and check_out times if enabled
            let checkInTime = null;
            let checkOutTime = null;
            if (saveSettingsTime) {
                try {
                    const settingsRes = await hrmAPI.getSettings();
                    const settings = settingsRes.data.data || settingsRes.data;
                    checkInTime = settings.check_in_time || null;
                    checkOutTime = settings.check_out_time || null;
                } catch (err) {
                    console.error('Error fetching settings:', err);
                }
            }
            
            if (rowData.hasRecord && rowData.id) {
                // Update existing
                await hrmAPI.updateMovement(rowData.id, {
                    employee_id: employeeId,
                    date: toLocalDateString(gridDate),
                    description: description,
                    purpose: rowData.purpose
                });
            } else {
                // Create new
                await hrmAPI.createMovement({
                    employee_id: employeeId,
                    date: toLocalDateString(gridDate),
                    description: description,
                    purpose: rowData.purpose
                });
            }

            // Create or update attendance record if checkbox is checked
            if (createAttendance) {
                const status = description === 'Absent' ? 'absent' : 'present';
                const dateStr = toLocalDateString(gridDate);
                
                // Check if attendance already exists for this employee on this date
                const attendanceData = {
                    employee_id: employeeId,
                    date: dateStr,
                    status: status,
                    remarks: rowData.purpose || description
                };
                
                // Add check_in and check_out times if settings time is enabled
                if (saveSettingsTime && checkInTime) {
                    attendanceData.check_in = checkInTime;
                    attendanceData.check_out = checkOutTime;
                }
                
                // Try to find existing attendance
                try {
                    const attRes = await hrmAPI.getAttendances();
                    const allAttendances = attRes.data.data || attRes.data;
                    const existingAttendance = allAttendances.find(a => 
                        a.employee_id === employeeId && 
                        a.date === dateStr
                    );
                    
                    if (existingAttendance) {
                        // Update existing attendance
                        await hrmAPI.updateAttendance(existingAttendance.id, attendanceData);
                    } else {
                        // Create new attendance
                        await hrmAPI.createAttendance(attendanceData);
                    }
                } catch (attErr) {
                    // If getAttendances fails, try to create anyway
                    console.error('Error checking attendance:', attErr);
                    try {
                        await hrmAPI.createAttendance(attendanceData);
                    } catch (createErr) {
                        console.error('Error creating attendance:', createErr);
                    }
                }
            }
            
            // Refresh data
            await fetchData();
            
            // Update local grid data
            setGridData(prev => ({
                ...prev,
                [employeeId]: {
                    ...prev[employeeId],
                    hasRecord: true,
                    id: prev[employeeId].id || Date.now() // Temporary ID until refresh
                }
            }));
            
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save');
        } finally {
            setSavingRows({...savingRows, [employeeId]: false});
        }
    };

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const days = getDaysInMonth(currentDate);

    // If grid view is active, show the Excel-like page
    if (showGridView && gridDate) {
        // Get employees in the ordered sequence
        const orderedEmployees = orderedEmployeeIds
            .map(id => employees.find(emp => emp.id === id))
            .filter(emp => emp !== undefined);
        
        return (
            <div>
                <div className="mb-6 flex items-center justify-between">
                    <div className="flex items-center">
                        <button onClick={() => setShowGridView(false)} className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg">
                            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
                                Movements - {gridDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </h1>
                            <label className="flex items-center mt-2">
                                <input 
                                    type="checkbox" 
                                    checked={createAttendance} 
                                    onChange={(e) => setCreateAttendance(e.target.checked)}
                                    className="mr-2 w-4 h-4"
                                />
                                <span className="text-sm text-gray-600 dark:text-gray-300">Record add on Attendance</span>
                            </label>
                            <label className="flex items-center mt-2 ml-6">
                                <input 
                                    type="checkbox" 
                                    checked={saveSettingsTime} 
                                    onChange={(e) => setSaveSettingsTime(e.target.checked)}
                                    className="mr-2 w-4 h-4"
                                />
                                <span className="text-sm text-gray-600 dark:text-gray-300">Save Settings Time</span>
                            </label>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Drag rows to reorder for PDF export</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={handleResetOrder} 
                            className="inline-flex items-center px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm"
                            title="Reset row order to default"
                        >
                            Reset Order
                        </button>
                        <button 
                            onClick={handleExportGridPDF} 
                            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                        >
                            <FileDown className="w-4 h-4 mr-2" /> Export PDF
                        </button>
                    </div>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

                <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase w-12">Sort</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase w-16">SL</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Employee Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Purpose</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase w-32">Status</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase w-24">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {orderedEmployees.map((emp, index) => {
                                    const rowData = gridData[emp.id] || {};
                                    const isDragging = draggedItem === index;
                                    return (
                                        <tr 
                                            key={emp.id} 
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragEnter={(e) => handleDragEnter(e, index)}
                                            onDragEnd={handleSort}
                                            onDragOver={(e) => e.preventDefault()}
                                            className={`hover:bg-gray-50 dark:hover:bg-slate-600 cursor-move ${isDragging ? 'opacity-50 bg-indigo-50 dark:bg-indigo-900/30' : ''}`}
                                        >
                                            <td className="px-2 py-3 text-center text-gray-400">
                                                <GripVertical className="w-4 h-4 inline" />
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{index + 1}</td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-100">{emp.name}</td>
                                            <td className="px-4 py-3">
                                                <input 
                                                    type="text" 
                                                    value={rowData.description || ''}
                                                    onChange={(e) => setGridData({
                                                        ...gridData,
                                                        [emp.id]: {...rowData, description: e.target.value}
                                                    })}
                                                    placeholder={rowData.description === '' ? 'Absent' : 'Enter description...'}
                                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-500 rounded bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <input 
                                                    type="text" 
                                                    value={rowData.purpose || ''}
                                                    onChange={(e) => setGridData({
                                                        ...gridData,
                                                        [emp.id]: {...rowData, purpose: e.target.value}
                                                    })}
                                                    placeholder="Enter purpose..."
                                                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-500 rounded bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                {rowData.hasRecord ? (
                                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Saved</span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Pending</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <button 
                                                    onClick={() => handleGridRowSave(emp.id)}
                                                    disabled={savingRows[emp.id]}
                                                    className="inline-flex items-center px-3 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
                                                >
                                                    {savingRows[emp.id] ? (
                                                        <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                                                    ) : (
                                                        <Save className="w-3 h-3 mr-1" />
                                                    )}
                                                    {rowData.hasRecord ? 'Update' : 'Save'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* PDF Preview Modal */}
                {previewUrl && (
                    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] mx-4 flex flex-col">
                            <div className="flex items-center justify-between p-4 border-b">
                                <h2 className="text-xl font-semibold text-gray-800">PDF Preview - {previewFilename}</h2>
                                <button onClick={closePreview} className="p-2 hover:bg-gray-100 rounded-lg">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="flex-1 p-4">
                                <iframe 
                                    src={previewUrl} 
                                    className="w-full h-full border rounded"
                                    title="PDF Preview"
                                />
                            </div>
                            <div className="flex justify-end gap-3 p-4 border-t">
                                <button onClick={closePreview} className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                                <button onClick={handleDownloadPDF} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center">
                                    <Download className="w-4 h-4 mr-2" /> Download
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Movements</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Track employee movements</p>
                </div>
                <div className="mt-4 sm:mt-0 flex gap-2">
                    <button onClick={handleExportPDF} className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg">
                        <FileDown className="w-4 h-4 mr-2" /> Export PDF
                    </button>
                    <button onClick={() => setShowCopyDayModal(true)} className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
                        <Copy className="w-4 h-4 mr-2" /> Copy Day
                    </button>
                    <button onClick={() => { resetForm(); setShowModal(true); }} className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                        <Plus className="w-4 h-4 mr-2" /> Add Movement
                    </button>
                </div>
            </div>

            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

            {/* Calendar View */}
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow mb-6">
                {/* Calendar Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-600">
                    <div className="flex items-center gap-2">
                        <button onClick={prevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg">
                            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                        </button>
                        <button onClick={() => setCurrentDate(new Date())} className="px-3 py-1 text-sm bg-indigo-100 dark:bg-indigo-800 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200 font-medium">
                            Today
                        </button>
                        <button onClick={nextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg">
                            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                        </button>
                    </div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                        {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h2>
                </div>
                
                {/* Calendar Grid */}
                <div className="p-4">
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {dayNames.map(day => (
                            <div key={day} className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2">
                                {day}
                            </div>
                        ))}
                    </div>
                    
                    {/* Calendar days */}
                    <div className="grid grid-cols-7 gap-1">
                        {days.map((day, index) => {
                            const dayMovements = getMovementsForDate(day);
                            const isToday = day && day.toDateString() === new Date().toDateString();
                            const isFriday = day && day.getDay() === 5; // Friday is holiday
                            const isHoliday = day && isHolidayDate(day, holidays);
                            const isHolidayOrFriday = isFriday || isHoliday;
                            const dateStr = day ? toLocalDateString(day) : '';
                            
                            return (
                                <div 
                                    key={index}
                                    onClick={() => handleDateClick(day)}
                                    className={`
                                        min-h-[80px] p-2 border rounded-lg cursor-pointer transition-colors
                                        ${day ? 'hover:bg-gray-50 dark:hover:bg-slate-600 border-gray-200 dark:border-slate-600' : 'border-transparent'}
                                        ${isToday ? 'bg-indigo-50 dark:bg-indigo-900 border-indigo-300 dark:border-indigo-700' : ''}
                                        ${isHolidayOrFriday ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700' : ''}
                                    `}
                                >
                                    {day && (
                                        <>
                                            <div className="text-sm font-medium ${isToday ? 'text-indigo-600 dark:text-indigo-400' : isHolidayOrFriday ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}">
                                                {day.getDate()}
                                            </div>
                                            {isHolidayOrFriday ? (
                                                <div className="mt-1">
                                                    <div className="text-xs bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300 px-1 py-0.5 rounded truncate font-medium">
                                                        {holidays.find(h => {
                                                            const hDate = h.date;
                                                            if (h.end_date) {
                                                                return dateStr >= hDate && dateStr <= h.end_date;
                                                            }
                                                            return hDate === dateStr;
                                                        })?.name || 'Holiday'}
                                                    </div>
                                                    {dayMovements.filter(m => m.description && m.description !== 'Absent').length > 0 && (
                                                        <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                                                            Attend: {String(dayMovements.filter(m => m.description && m.description !== 'Absent').length).padStart(2, '0')} Employees
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="mt-1">
                                                    {(() => {
                                                        // Only show if there are actual movements
                                                        if (dayMovements.length === 0) return null;
                                                        
                                                        const absent = dayMovements.filter(m => m.description === 'Absent').length;
                                                        const attend = dayMovements.filter(m => m.description && m.description !== 'Absent').length;
                                                        const notDone = employees.length - dayMovements.length;
                                                        
                                                        return (
                                                            <>
                                                                {attend > 0 && (
                                                                    <div className="text-xs text-green-600 dark:text-green-400">
                                                                        Attend: {String(attend).padStart(2, '0')} Employees
                                                                    </div>
                                                                )}
                                                                {absent > 0 && (
                                                                    <div className="text-xs text-red-600 dark:text-red-400">
                                                                        Absent: {String(absent).padStart(2, '0')} Employees
                                                                    </div>
                                                                )}
                                                                {notDone > 0 && (
                                                                    <div className="text-xs text-yellow-600 dark:text-yellow-400">
                                                                        Not Done: {String(notDone).padStart(2, '0')} Employees
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Search and Filter */}
            <div className="mb-4 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input type="text" placeholder="Search movements..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" />
                </div>
                <div className="flex items-center gap-2">
                    <input 
                        type="date" 
                        value={filterDate || ''} 
                        onChange={(e) => setFilterDate(e.target.value || null)}
                        className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" 
                    />
                    <button 
                        onClick={() => setFilterDate(toLocalDateString(new Date()))}
                        className="px-3 py-2 text-sm bg-indigo-100 dark:bg-indigo-800 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200"
                    >
                        Today
                    </button>
                    {filterDate && (
                        <button 
                            onClick={() => setFilterDate(null)}
                            className="px-3 py-2 text-sm bg-gray-200 dark:bg-slate-600 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-500"
                        >
                            All
                        </button>
                    )}
                </div>
            </div>

            {/* Table View */}
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Employee</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Purpose</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {filteredMovements.length > 0 ? filteredMovements.map(mov => (
                                    <tr key={mov.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">{getEmployeeName(mov.employee_id)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{mov.date && mov.date.split('T')[0]}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{mov.description}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{mov.purpose || '-'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <button onClick={() => handleDuplicate(mov)} className="text-blue-600 hover:text-blue-900 mr-3" title="Duplicate">
                                                <Copy className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => handleEdit(mov)} className="text-indigo-600 hover:text-indigo-900 mr-3"><Edit className="w-4 h-4" /></button>
                                            <button onClick={() => handleDelete(mov.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No records found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">{editingId ? 'Edit' : 'Add'} Movement</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employee</label>
                                <select value={formData.employee_id} onChange={(e) => setFormData({...formData, employee_id: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required>
                                    <option value="">Select Employee</option>
                                    {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                                <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" rows="2" required />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Purpose</label>
                                <input type="text" value={formData.purpose} onChange={(e) => setFormData({...formData, purpose: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                            </div>
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Duplicate Modal */}
            {showDuplicateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">Duplicate Movement</h2>
                        <form onSubmit={handleDuplicateSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Employees</label>
                                <div className="border rounded-lg max-h-40 overflow-y-auto p-2 bg-white dark:bg-slate-800">
                                    {employees.map(e => (
                                        <label key={e.id} className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-slate-700 rounded cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                checked={duplicateData.employee_ids.includes(e.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setDuplicateData({...duplicateData, employee_ids: [...duplicateData.employee_ids, e.id]});
                                                    } else {
                                                        setDuplicateData({...duplicateData, employee_ids: duplicateData.employee_ids.filter(id => id !== e.id)});
                                                    }
                                                }}
                                                className="mr-2"
                                            />
                                            <span className="text-gray-800 dark:text-gray-200">{e.name}</span>
                                        </label>
                                    ))}
                                </div>
                                {duplicateData.employee_ids.length === 0 && (
                                    <p className="text-red-500 text-sm mt-1">Please select at least one employee</p>
                                )}
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                                <input type="date" value={duplicateData.date} onChange={(e) => setDuplicateData({...duplicateData, date: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                <textarea value={duplicateData.description} onChange={(e) => setDuplicateData({...duplicateData, description: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" rows="2" required />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Purpose</label>
                                <input type="text" value={duplicateData.purpose} onChange={(e) => setDuplicateData({...duplicateData, purpose: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                            </div>
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={() => setShowDuplicateModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600">Cancel</button>
                                <button type="submit" disabled={duplicateData.employee_ids.length === 0} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Create {duplicateData.employee_ids.length > 0 ? `for ${duplicateData.employee_ids.length} Employee(s)` : 'Movement'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Copy Day Modal */}
            {showCopyDayModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">Copy All Movements</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Copy all movements from one day to another</p>
                        <form onSubmit={handleCopyDaySubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Date (Source)</label>
                                <input type="date" value={copyDayData.from_date} onChange={(e) => setCopyDayData({...copyDayData, from_date: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To Date (Destination)</label>
                                <input type="date" value={copyDayData.to_date} onChange={(e) => setCopyDayData({...copyDayData, to_date: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required />
                            </div>
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={() => setShowCopyDayModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                    Copy All
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* PDF Preview Modal */}
            {previewUrl && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] mx-4 flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h2 className="text-xl font-semibold text-gray-800">PDF Preview - {previewFilename}</h2>
                            <button onClick={closePreview} className="p-2 hover:bg-gray-100 rounded-lg">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 p-4">
                            <iframe 
                                src={previewUrl} 
                                className="w-full h-full border rounded"
                                title="PDF Preview"
                            />
                        </div>
                        <div className="flex justify-end gap-3 p-4 border-t">
                            <button onClick={closePreview} className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                            <button onClick={handleDownloadPDF} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center">
                                <Download className="w-4 h-4 mr-2" /> Download
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Movements;
